import React, { useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { blogPosts } from '../data/blogs'
import { ArrowLeft, Clock, Calendar, Share2, Facebook, Twitter, Linkedin } from 'lucide-react'
import SEO from '../components/SEO'

const BlogPostPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>()
    const navigate = useNavigate()
    const post = blogPosts.find(p => p.slug === slug)
    const relatedPosts = blogPosts.filter(p => p.slug !== slug && p.category === post?.category).slice(0, 2)

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [slug])

    if (!post) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
                    <Link to="/blogs" className="text-primary-600 hover:text-primary-700 font-medium">
                        ← Back to Articles
                    </Link>
                </div>
            </div>
        )
    }

    const handleShare = (platform: string) => {
        const url = window.location.href
        const text = `Check out this article: ${post.title}`

        switch (platform) {
            case 'facebook':
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank')
                break
            case 'twitter':
                window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank')
                break
            case 'linkedin':
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank')
                break
        }
    }



    return (
        <div className="min-h-screen bg-white">
            <SEO
                title={post.title}
                description={post.excerpt}
                image={post.image}
                type="article"
                keywords={`${post.category}, real estate, ${post.slug.replace(/-/g, ', ')}`}
            />
            {/* ProgressBar (Optional enhancement) */}

            {/* Hero Header */}
            <div className="relative h-[60vh] md:h-[70vh] w-full overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-20">
                    <div className="max-w-4xl mx-auto">
                        <Link to="/blogs" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
                            <ArrowLeft className="w-5 h-5 mr-2" />
                            Back to Insights
                        </Link>

                        <div className="flex flex-wrap gap-4 items-center mb-6">
                            <span className="bg-primary-500 text-black px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wider">
                                {post.category}
                            </span>
                            <div className="flex items-center text-white/90 text-sm md:text-base font-medium">
                                <Calendar className="w-4 h-4 mr-2" />
                                {post.date}
                            </div>
                            <div className="flex items-center text-white/90 text-sm md:text-base font-medium">
                                <Clock className="w-4 h-4 mr-2" />
                                {post.readTime}
                            </div>
                        </div>

                        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white font-bricolage leading-tight mb-8">
                            {post.title}
                        </h1>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

                    {/* Main Content */}
                    <div className="lg:col-span-2">
                        <article className="prose prose-lg md:prose-xl max-w-none text-gray-700 font-sans leading-relaxed">
                            <p className="text-xl md:text-2xl text-gray-500 font-medium mb-10 leading-relaxed border-l-4 border-primary-500 pl-6 not-italic">
                                {post.excerpt}
                            </p>

                            {post.fullContent.split('\n').map((paragraph, index) => {
                                // Basic formatting detection (bolding headers)
                                const isHeader = paragraph.startsWith('**') && paragraph.endsWith('**') || paragraph.length < 50 && paragraph.includes('?'); // Simple heuristic

                                if (isHeader) {
                                    return <h3 key={index} className="text-2xl font-bold text-gray-900 mt-10 mb-6 font-bricolage">{paragraph.replace(/\*\*/g, '')}</h3>
                                }

                                if (paragraph.startsWith('*')) {
                                    return <li key={index} className="ml-4 mb-2">{paragraph.replace(/^\*\s*/, '')}</li>
                                }

                                return paragraph.trim() && (
                                    <p key={index} className="mb-6 text-gray-700">
                                        {paragraph.split(/(\*\*.*?\*\*)/).map((part, i) =>
                                            part.startsWith('**') && part.endsWith('**')
                                                ? <strong key={i} className="font-bold text-gray-900">{part.slice(2, -2)}</strong>
                                                : part
                                        )}
                                    </p>
                                )
                            })}
                        </article>

                        {/* Share Section */}
                        <div className="mt-16 pt-8 border-t border-gray-100">
                            <h4 className="text-lg font-bold text-gray-900 mb-4">Share this article</h4>
                            <div className="flex gap-4">
                                <button onClick={() => handleShare('facebook')} className="p-3 bg-blue-600 text-white rounded-full hover:opacity-90 transition-opacity">
                                    <Facebook className="w-5 h-5" />
                                </button>
                                <button onClick={() => handleShare('twitter')} className="p-3 bg-sky-500 text-white rounded-full hover:opacity-90 transition-opacity">
                                    <Twitter className="w-5 h-5" />
                                </button>
                                <button onClick={() => handleShare('linkedin')} className="p-3 bg-blue-700 text-white rounded-full hover:opacity-90 transition-opacity">
                                    <Linkedin className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(window.location.href)
                                        // Optional: Show toast
                                    }}
                                    className="p-3 bg-gray-800 text-white rounded-full hover:opacity-90 transition-opacity"
                                >
                                    <Share2 className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-12">

                        {/* Author/Brand Bio */}
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100">
                            <div className="flex items-center gap-4 mb-4">
                                <img src="/logo.png" alt="Singh's Best Buy" className="w-12 h-12 object-contain bg-white rounded-full p-1" />
                                <div>
                                    <h4 className="font-bold text-gray-900">Singh's Best Buy</h4>
                                    <p className="text-xs text-gray-500 uppercase tracking-wider">Editorial Team</p>
                                </div>
                            </div>
                            <p className="text-gray-600 text-sm leading-relaxed mb-6">
                                Providing expert insights into Gurgaon's real estate market. We decode complex trends to help you invest with confidence.
                            </p>
                            <Link to="/contact" className="block w-full py-3 bg-primary-500 text-white text-center font-bold rounded-lg hover:bg-primary-600 transition-colors">
                                Talk to an Expert
                            </Link>
                        </div>

                        {/* Related/Latest Posts */}
                        <div>
                            <h3 className="font-bricolage font-bold text-2xl text-gray-900 mb-6">More Insights</h3>
                            <div className="space-y-6">
                                {blogPosts.filter(p => p.id !== post.id).slice(0, 4).map(sidebarPost => (
                                    <Link key={sidebarPost.id} to={`/blog/${sidebarPost.slug}`} className="group flex gap-4 items-start">
                                        <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
                                            <img
                                                src={sidebarPost.image}
                                                alt={sidebarPost.title}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                            />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 leading-tight group-hover:text-primary-600 transition-colors mb-2">
                                                {sidebarPost.title}
                                            </h4>
                                            <span className="text-xs text-gray-500">{sidebarPost.date}</span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {/* Categories Box */}
                        <div className="bg-gray-900 text-white p-8 rounded-2xl">
                            <h3 className="font-bricolage font-bold text-xl mb-6">Topics</h3>
                            <div className="flex flex-wrap gap-2">
                                {Array.from(new Set(blogPosts.map(p => p.category))).map(cat => (
                                    <span key={cat} className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded-full text-sm transition-colors cursor-pointer border border-white/10">
                                        {cat}
                                    </span>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* Related Posts Bottom Grid (Mobile friendly) */}
            {relatedPosts.length > 0 && (
                <div className="bg-gray-50 py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 className="text-3xl font-bold font-bricolage mb-8">Related Articles</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {relatedPosts.map(relPost => (
                                <Link key={relPost.id} to={`/blog/${relPost.slug}`} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all group">
                                    <div className="h-48 overflow-hidden">
                                        <img src={relPost.image} alt={relPost.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    </div>
                                    <div className="p-6">
                                        <h3 className="font-bold text-xl mb-2 group-hover:text-primary-600">{relPost.title}</h3>
                                        <p className="text-gray-600 line-clamp-2">{relPost.excerpt}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
}

export default BlogPostPage
